/**
 * VÉRAULT Portal — Advisor: Master Calendar (Pass B Task #6)
 *
 * The operational heartbeat of the ecosystem. Calm monthly grid surfaced
 * over a smart event stream — departures, returns, hotel check-ins,
 * flights, payment deadlines, agreement milestones, planning deadlines,
 * concierge reminders.
 *
 * Filtering / visibility / urgency are first-class. Each chip toggles a
 * category in the legend. "Urgent" combines overdue payments + past-due
 * agreements + planning deadlines ≤14 days out.
 *
 * Single source: GET /api/advisor/calendar/events?start=&end=.
 */
import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Plane,
  Hotel,
  CreditCard,
  FileSignature,
  AlertCircle,
  Sparkles,
  Compass,
  Plane as PlaneIcon,
  Filter,
  PhoneCall,
} from "lucide-react";
import ConsultationCompleteModal from "./ConsultationCompleteModal";
import {
  PageHeader,
  Surface,
  EditorialRule,
  Heading,
  Empty,
  Skeleton,
  PortalButton,
  api,
  fmt,
  motion as portalMotion,
} from "../../index";

const { fadeInUp, stagger } = portalMotion;

// Type → visual config (color + icon + label)
const TYPE_CONFIG = {
  departure: { label: "Departure", color: "hsl(var(--accent-gold))", icon: Plane },
  return: { label: "Return", color: "hsl(var(--accent-gold))", icon: PlaneIcon },
  flight: { label: "Flight", color: "hsl(var(--accent-gold))", icon: Plane },
  hotel: { label: "Hotel", color: "hsl(168, 22%, 42%)", icon: Hotel },
  payment: { label: "Payment", color: "hsl(20, 60%, 50%)", icon: CreditCard },
  agreement: { label: "Agreement", color: "hsl(280, 22%, 45%)", icon: FileSignature },
  planning: { label: "Planning", color: "hsl(35, 70%, 55%)", icon: Compass },
  concierge: { label: "Reminder", color: "hsl(210, 30%, 50%)", icon: Sparkles },
  consultation: { label: "Consultation", color: "hsl(265, 35%, 50%)", icon: PhoneCall },
};

const TYPE_GROUPS = [
  { id: "travel", label: "Travel dates", types: ["departure", "return", "flight"] },
  { id: "hotel", label: "Hotels", types: ["hotel"] },
  { id: "payment", label: "Payments", types: ["payment"] },
  { id: "agreement", label: "Agreements", types: ["agreement"] },
  { id: "planning", label: "Planning", types: ["planning"] },
  { id: "consultation", label: "Consultations", types: ["consultation"] },
  { id: "concierge", label: "Concierge", types: ["concierge"] },
];

const MONTH_LABEL = (d) =>
  d.toLocaleString("en-US", { month: "long", year: "numeric" });

function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function startOfCalendarGrid(d) {
  const start = startOfMonth(d);
  const day = start.getDay(); // 0..6 (Sun..Sat)
  return new Date(start.getFullYear(), start.getMonth(), 1 - day);
}
function toISO(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function addMonths(d, n) {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}

export default function AdvisorCalendar() {
  const [cursor, setCursor] = React.useState(() => startOfMonth(new Date()));
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [completeOpen, setCompleteOpen] = React.useState(false);
  const [activeConsult, setActiveConsult] = React.useState(null);
  const [activeTypes, setActiveTypes] = React.useState(() => new Set(Object.keys(TYPE_CONFIG)));
  // Wave 26.12 — Mode toggle: "trips" (clean — just travel + bands) or
  // "operations" (everything: hotels, payments, agreements, reminders).
  const [mode, setMode] = React.useState(() => {
    try { return localStorage.getItem("verault.calendar.mode") || "operations"; }
    catch { return "operations"; }
  });
  React.useEffect(() => {
    try { localStorage.setItem("verault.calendar.mode", mode); } catch { /* ignore */ }
    if (mode === "trips") {
      setActiveTypes(new Set(["departure", "return", "flight"]));
    } else {
      setActiveTypes(new Set(Object.keys(TYPE_CONFIG)));
    }
  }, [mode]);
  const [showUrgentOnly, setShowUrgentOnly] = React.useState(false);
  const [selectedDay, setSelectedDay] = React.useState(null);

  const monthStart = startOfMonth(cursor);
  const gridStart = startOfCalendarGrid(cursor);

  // Fetch a window that covers visible + a comfy buffer
  const [refreshKey, setRefreshKey] = React.useState(0);
  React.useEffect(() => {
    const bump = () => setRefreshKey((k) => k + 1);
    window.addEventListener("vt:calendar-refresh", bump);
    return () => window.removeEventListener("vt:calendar-refresh", bump);
  }, []);
  React.useEffect(() => {
    let alive = true;
    const fetchStart = toISO(new Date(monthStart.getFullYear(), monthStart.getMonth() - 1, 1));
    const fetchEnd = toISO(new Date(monthStart.getFullYear(), monthStart.getMonth() + 2, 0));
    setData(null);
    api
      .get("/advisor/calendar/events", { params: { start: fetchStart, end: fetchEnd } })
      .then(({ data }) => {
        if (alive) setData(data);
      })
      .catch(() => alive && setError("Couldn't load the calendar."));
    return () => {
      alive = false;
    };
  }, [monthStart.getFullYear(), monthStart.getMonth(), refreshKey]); // eslint-disable-line

  const filteredEvents = React.useMemo(() => {
    if (!data?.events) return [];
    return data.events.filter((e) => {
      if (!activeTypes.has(e.type)) return false;
      if (showUrgentOnly && !e.urgent) return false;
      return true;
    });
  }, [data, activeTypes, showUrgentOnly]);

  // Group events by date
  const eventsByDate = React.useMemo(() => {
    const m = {};
    filteredEvents.forEach((e) => {
      if (!m[e.date]) m[e.date] = [];
      m[e.date].push(e);
    });
    return m;
  }, [filteredEvents]);

  // Wave 26.11 — Trip duration bands. For each departure event with an
  // end_date, render a low-saturation strip across every day in [start,end].
  // Cell-level rendering keeps layout simple while reading as a continuous
  // ribbon thanks to consistent height + adjacent cells.
  const tripBandsByDate = React.useMemo(() => {
    const m = {};
    if (!data) return m;
    const seen = new Set();
    for (const ev of data.events || []) {
      if (ev.type !== "departure" || !ev.end_date) continue;
      if (!activeTypes.has("departure")) continue;
      const tid = ev.trip_id;
      if (seen.has(tid)) continue;
      seen.add(tid);
      const start = ev.date;
      const end = ev.end_date;
      // Build the inclusive date list. Strings sort lexicographically since ISO.
      try {
        const cur = new Date(start + "T00:00:00");
        const last = new Date(end + "T00:00:00");
        while (cur <= last) {
          const iso = toISO(cur);
          if (!m[iso]) m[iso] = [];
          m[iso].push({
            trip_id: tid,
            title: ev.title.replace(/^Departure · /, ""),
            client_name: ev.client_name,
            start, end,
            is_first: iso === start,
            is_last: iso === end,
          });
          cur.setDate(cur.getDate() + 1);
        }
      } catch { /* skip malformed dates */ }
    }
    return m;
  }, [data, activeTypes]);

  const days = React.useMemo(() => {
    const out = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + i);
      out.push(d);
    }
    return out;
  }, [gridStart]);

  const toggleType = (type) => {
    setActiveTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  };
  const toggleGroup = (group) => {
    const allOn = group.types.every((t) => activeTypes.has(t));
    setActiveTypes((prev) => {
      const next = new Set(prev);
      if (allOn) group.types.forEach((t) => next.delete(t));
      else group.types.forEach((t) => next.add(t));
      return next;
    });
  };

  const today = toISO(new Date());
  const isCurrentMonth = (d) => d.getMonth() === monthStart.getMonth();

  // Urgency total for the "Urgent" toggle badge
  const urgentCount = (data?.events || []).filter((e) => e.urgent).length;

  return (
    <motion.div initial="initial" animate="animate" variants={stagger} className="space-y-10">
      <motion.div variants={fadeInUp}>
        <PageHeader
          eyebrow="Master calendar"
          title="The operational heartbeat."
          lede="Departures, returns, payments, agreements, planning deadlines — every moving piece, layered intelligently."
          actions={
            <div className="flex items-center gap-2 flex-wrap">
              <PortalButton
                variant="ghost"
                size="sm"
                icon={<ChevronLeft className="size-3.5" />}
                onClick={() => setCursor(addMonths(cursor, -1))}
                data-testid="calendar-prev-month"
              >
                Prev
              </PortalButton>
              <PortalButton
                variant="ghost"
                size="sm"
                onClick={() => setCursor(startOfMonth(new Date()))}
                data-testid="calendar-today"
              >
                Today
              </PortalButton>
              <PortalButton
                variant="ghost"
                size="sm"
                iconAfter={<ChevronRight className="size-3.5" />}
                onClick={() => setCursor(addMonths(cursor, 1))}
                data-testid="calendar-next-month"
              >
                Next
              </PortalButton>
            </div>
          }
        />
      </motion.div>

      {/* Filter rail */}
      <motion.div variants={fadeInUp}>
        <Surface variant="paper" padded>
          {/* Wave 26.12 — Mode toggle: Trips view (clean ribbons) vs Operations (everything) */}
          <div className="flex items-center justify-between gap-3 flex-wrap mb-3 pb-3 border-b border-[rgba(0,0,0,0.05)]">
            <div className="inline-flex p-0.5 rounded-full bg-[hsl(var(--background-paper))] border border-[rgba(0,0,0,0.06)]" data-testid="calendar-mode-toggle">
              {[
                { id: "trips", label: "Trips", desc: "Clean view — just travel dates" },
                { id: "operations", label: "Operations", desc: "Everything: hotels, payments, agreements" },
              ].map((m) => (
                <button
                  key={m.id}
                  onClick={() => setMode(m.id)}
                  title={m.desc}
                  data-testid={`calendar-mode-${m.id}`}
                  className={`px-4 py-1.5 rounded-full font-mono text-[10.5px] uppercase tracking-[0.2em] transition-colors ${
                    mode === m.id
                      ? "bg-white text-[hsl(var(--foreground))] shadow-sm"
                      : "text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))]"
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>
            <p className="font-accent italic text-[12px] text-[hsl(var(--foreground-muted))]">
              {mode === "trips" ? "Showing just trip dates as continuous ribbons." : "Showing everything on the calendar."}
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap" data-testid="calendar-filters">
            <Filter className="size-3.5 text-[hsl(var(--foreground-muted))]" strokeWidth={1.5} />
            <button
              onClick={() => setShowUrgentOnly((v) => !v)}
              data-testid="calendar-filter-urgent"
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-mono text-[10.5px] uppercase tracking-[0.2em] border transition-colors ${
                showUrgentOnly
                  ? "border-[hsl(var(--accent-terracotta))] bg-[hsl(var(--accent-terracotta))]/10 text-[hsl(var(--accent-terracotta))]"
                  : "border-[rgba(0,0,0,0.08)] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))]"
              }`}
            >
              <AlertCircle className="size-3" strokeWidth={1.5} />
              Urgent · {urgentCount}
            </button>
            {TYPE_GROUPS.map((g) => {
              const active = g.types.every((t) => activeTypes.has(t));
              return (
                <button
                  key={g.id}
                  onClick={() => toggleGroup(g)}
                  data-testid={`calendar-filter-${g.id}`}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-mono text-[10.5px] uppercase tracking-[0.2em] border transition-colors ${
                    active
                      ? "border-[hsl(var(--accent-gold))] bg-[hsl(var(--accent-gold))]/10 text-[hsl(var(--foreground))]"
                      : "border-[rgba(0,0,0,0.08)] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))]"
                  }`}
                >
                  <span
                    className="size-1.5 rounded-full"
                    style={{ background: TYPE_CONFIG[g.types[0]].color }}
                    aria-hidden
                  />
                  {g.label}
                </button>
              );
            })}
          </div>
        </Surface>
      </motion.div>

      {/* Month label */}
      <motion.div variants={fadeInUp}>
        <div className="flex items-center justify-between">
          <Heading level="h3" className="font-serif text-2xl">
            {MONTH_LABEL(monthStart)}
          </Heading>
          <p className="font-mono text-[10.5px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
            {data ? `${filteredEvents.length} visible event${filteredEvents.length === 1 ? "" : "s"}` : "loading"}
          </p>
        </div>
      </motion.div>

      {/* Calendar grid */}
      <motion.div variants={fadeInUp}>
        {error ? (
          <Empty title={error} icon={<AlertCircle className="size-6" strokeWidth={1.25} />} />
        ) : !data ? (
          <Skeleton className="h-96" />
        ) : (
          <Surface variant="default" padded={false} className="overflow-hidden">
            <div className="grid grid-cols-7 border-b border-[rgba(0,0,0,0.05)] bg-[hsl(var(--background-paper))]/40">
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
                <div
                  key={d}
                  className="px-3 py-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] text-center"
                >
                  {d}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7" data-testid="calendar-grid">
              {days.map((d, i) => {
                const iso = toISO(d);
                const dayEvents = eventsByDate[iso] || [];
                const tripBands = tripBandsByDate[iso] || [];
                const isToday = iso === today;
                const isOtherMonth = !isCurrentMonth(d);
                const hasUrgent = dayEvents.some((e) => e.urgent);
                return (
                  <button
                    key={i}
                    onClick={() => setSelectedDay(iso)}
                    data-testid={`calendar-day-${iso}`}
                    className={`relative min-h-[120px] pb-3 border-r border-b border-[rgba(0,0,0,0.05)] p-2 text-left transition-colors ${
                      isOtherMonth ? "bg-[hsl(var(--background-paper))]/30 text-[hsl(var(--foreground-muted))]" : "hover:bg-[hsl(var(--background-paper))]/50"
                    } ${isToday ? "ring-1 ring-inset ring-[hsl(var(--accent-gold))]" : ""}`}
                  >
                    <div className="flex items-center justify-between">
                      <span
                        className={`font-mono text-[12px] ${
                          isToday
                            ? "text-[hsl(var(--accent-gold))] font-semibold"
                            : isOtherMonth
                            ? "text-[hsl(var(--foreground-muted))]"
                            : "text-[hsl(var(--foreground))]"
                        }`}
                      >
                        {d.getDate()}
                      </span>
                      {hasUrgent && (
                        <span
                          className="size-1.5 rounded-full bg-[hsl(var(--accent-terracotta))]"
                          aria-label="urgent"
                        />
                      )}
                    </div>
                    <div className="mt-1.5 space-y-1" data-testid={`calendar-day-${iso}-events`}>
                      {dayEvents.slice(0, 3).map((e) => {
                        const cfg = TYPE_CONFIG[e.type] || TYPE_CONFIG.planning;
                        return (
                          <div
                            key={e.id}
                            className={`flex items-start gap-1 truncate ${e.urgent ? "font-semibold" : ""}`}
                            title={e.title}
                            data-event-type={e.type}
                          >
                            <span
                              className="mt-1 size-1.5 rounded-full shrink-0"
                              style={{ background: cfg.color }}
                              aria-hidden
                              data-testid={`event-dot-${e.type}`}
                            />
                            <span className="font-body text-[11px] truncate text-[hsl(var(--foreground))]">
                              {e.title.replace(/^[^·]+·\s*/, "")}
                            </span>
                          </div>
                        );
                      })}
                      {dayEvents.length > 3 && (
                        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">
                          +{dayEvents.length - 3} more
                        </p>
                      )}
                    </div>

                    {/* Wave 26.11 — Trip duration bands at the bottom of the cell */}
                    {tripBands.length > 0 && (
                      <div className="absolute left-0 right-0 bottom-1 px-1 space-y-0.5 pointer-events-none" data-testid={`trip-bands-${iso}`}>
                        {tripBands.slice(0, 3).map((b, idx) => (
                          <div
                            key={`${b.trip_id}-${idx}`}
                            title={`${b.title}${b.client_name ? " · " + b.client_name : ""}`}
                            className={`h-1.5 bg-[hsl(var(--accent-gold))]/55 transition-opacity hover:opacity-100 ${
                              b.is_first ? "rounded-l-full" : ""
                            } ${b.is_last ? "rounded-r-full" : ""}`}
                            style={{
                              marginLeft: b.is_first ? "0" : "-8px",
                              marginRight: b.is_last ? "0" : "-8px",
                            }}
                            data-testid={`trip-band-${b.trip_id}`}
                          />
                        ))}
                        {tripBands.length > 3 && (
                          <p className="font-mono text-[8px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] text-center">
                            +{tripBands.length - 3}
                          </p>
                        )}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </Surface>
        )}
      </motion.div>

      {/* Day drawer */}
      {selectedDay && (
        <motion.div variants={fadeInUp}>
          <EditorialRule label={`Events on ${selectedDay}`} />
          <Surface variant="paper" padded className="mt-4">
            <div className="flex items-center justify-between mb-3">
              <Heading level="eyebrow">{selectedDay}</Heading>
              <button
                onClick={() => setSelectedDay(null)}
                data-testid="calendar-close-day"
                className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))]"
              >
                Close ✕
              </button>
            </div>
            <DayEvents
              events={eventsByDate[selectedDay] || []}
              onConsultationAction={async (consultId, action, eventData) => {
                try {
                  if (action === "cancel") {
                    if (!window.confirm("Cancel this consultation? The record stays in the audit log.")) return;
                    await api.patch(`/consultations/${consultId}`, { status: "cancelled" });
                    window.dispatchEvent(new Event("vt:calendar-refresh"));
                  } else if (action === "complete") {
                    // Phase 1.4 — open the rich completion modal.
                    try {
                      const { data } = await api.get(`/consultations`);
                      const rows = data?.rows || [];
                      const found = rows.find((r) => r.id === consultId);
                      setActiveConsult(found || { id: consultId, prospect_name: eventData?.client_name });
                      setCompleteOpen(true);
                    } catch {
                      setActiveConsult({ id: consultId, prospect_name: eventData?.client_name });
                      setCompleteOpen(true);
                    }
                  } else if (action === "reschedule") {
                    const next = window.prompt("New date/time (ISO format, e.g. 2026-07-15T15:00:00Z):");
                    if (!next) return;
                    await api.patch(`/consultations/${consultId}`, { status: "rescheduled", start_at: next });
                    window.dispatchEvent(new Event("vt:calendar-refresh"));
                  }
                } catch {
                  /* silent — calendar will refresh on next interaction */
                }
              }}
            />
          </Surface>
        </motion.div>
      )}

      {completeOpen && activeConsult && (
        <ConsultationCompleteModal
          consultation={activeConsult}
          onClose={() => { setCompleteOpen(false); setActiveConsult(null); }}
          onDone={() => { window.dispatchEvent(new Event("vt:calendar-refresh")); }}
        />
      )}
    </motion.div>
  );
}

function DayEvents({ events, onConsultationAction }) {
  if (events.length === 0) {
    return (
      <p className="font-accent italic text-[13.5px] text-[hsl(var(--foreground-secondary))]">
        Nothing on the books for this day.
      </p>
    );
  }
  return (
    <ul className="space-y-2" data-testid="calendar-day-events">
      {events.map((e) => {
        const cfg = TYPE_CONFIG[e.type] || TYPE_CONFIG.planning;
        const Icon = cfg.icon;
        const isConsult = e.type === "consultation";
        const consultId = isConsult && typeof e.id === "string" ? e.id.replace(/^consult-/, "") : null;
        const href = isConsult ? (e.href || null)
                   : e.trip_id ? `/portal/advisor/journeys/${e.trip_id}` : null;
        const Wrapper = href && !isConsult ? Link : "div";
        const wrapperProps = href && !isConsult ? { to: href } : {};
        return (
          <li key={e.id}>
            <Wrapper
              {...wrapperProps}
              className="group flex items-start gap-3 p-3 rounded-md bg-[hsl(var(--background))]/60 border border-[rgba(0,0,0,0.04)] hover:border-[hsl(var(--accent-gold))]/30 transition-colors"
              data-testid={`calendar-event-${e.id}`}
            >
              <span
                className="mt-0.5 size-7 inline-flex items-center justify-center rounded-full text-white shrink-0"
                style={{ background: cfg.color }}
              >
                <Icon className="size-3.5" strokeWidth={1.5} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-body text-[14px] text-[hsl(var(--foreground))] group-hover:text-[hsl(var(--accent-gold))] transition-colors">
                  {e.title}
                </p>
                <p className="mt-0.5 font-mono text-[10.5px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">
                  {[cfg.label, e.client_name, e.meta].filter(Boolean).join(" · ")}
                </p>
                {isConsult && consultId && (
                  <div className="mt-2 flex items-center gap-1 flex-wrap" data-testid={`consultation-actions-${consultId}`}>
                    <button
                      type="button"
                      onClick={(ev) => { ev.preventDefault(); onConsultationAction?.(consultId, "reschedule", e); }}
                      className="px-2 py-1 rounded-full bg-white border border-[rgba(0,0,0,0.06)] font-mono text-[9.5px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))]"
                      data-testid={`consultation-action-reschedule-${consultId}`}
                    >Reschedule</button>
                    <button
                      type="button"
                      onClick={(ev) => { ev.preventDefault(); onConsultationAction?.(consultId, "complete", e); }}
                      className="px-2 py-1 rounded-full bg-white border border-[rgba(0,0,0,0.06)] font-mono text-[9.5px] uppercase tracking-[0.18em] text-[hsl(168,22%,40%)] hover:text-[hsl(168,22%,30%)]"
                      data-testid={`consultation-action-complete-${consultId}`}
                    >Mark complete</button>
                    <button
                      type="button"
                      onClick={(ev) => { ev.preventDefault(); onConsultationAction?.(consultId, "cancel", e); }}
                      className="px-2 py-1 rounded-full bg-white border border-[rgba(0,0,0,0.06)] font-mono text-[9.5px] uppercase tracking-[0.18em] text-[hsl(var(--accent-terracotta))] hover:opacity-80"
                      data-testid={`consultation-action-cancel-${consultId}`}
                    >Cancel</button>
                  </div>
                )}
              </div>
              {e.urgent && (
                <AlertCircle className="size-4 text-[hsl(var(--accent-terracotta))] shrink-0" strokeWidth={1.5} />
              )}
            </Wrapper>
          </li>
        );
      })}
    </ul>
  );
}
